import { useCallback, useEffect, useState } from "react";
import { Play, Trash2, Eye, CheckCircle2, Settings2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { payrollApi, PAY_KINDS } from "@/api";
import { useLang, fmtMoney } from "@/i18n";
import { StatusBadge } from "@/components/Badges";
import { PayslipDialog } from "@/components/PayslipDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ApdCard } from "@/components/ApdCard";

const now = new Date();
const YEARS = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

export const PayslipsTab = () => {
  const { t, lang } = useLang();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [kind, setKind] = useState("all");
  const [runKind, setRunKind] = useState("salary");
  const [rows, setRows] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [running, setRunning] = useState(false);

  const monthName = (m) => new Date(2000, m - 1, 1).toLocaleDateString(lang === "el" ? "el-GR" : "en-GB", { month: "long" });

  const load = useCallback(() => {
    payrollApi.list({ year, month, kind }).then(setRows).catch(() => toast.error(t("error")));
  }, [year, month, kind, t]);
  useEffect(load, [load]);
  useEffect(() => { payrollApi.settings().then(setSettings); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const res = await payrollApi.run({ kind: runKind, year: Number(year), month: Number(month) });
      toast.success(`${res.created.length} ${t("payslips_created")}${res.skipped ? ` · ${res.skipped} ${t("payslips_skipped")}` : ""}`);
      load();
    } catch { toast.error(t("error")); } finally { setRunning(false); }
  };

  const markPaid = async (s) => {
    try { await payrollApi.setStatus(s.id, "paid"); toast.success(t("status_updated")); load(); } catch { toast.error(t("error")); }
  };
  const recalc = async (s) => {
    try { await payrollApi.recalculate(s.id); toast.success(t("recalculated")); load(); } catch { toast.error(t("error")); }
  };
  const remove = async () => {
    try { await payrollApi.remove(deleting.id); toast.success(t("payslip_deleted")); load(); } catch { toast.error(t("error")); } finally { setDeleting(null); }
  };
  const saveSettings = async () => {
    try {
      const s = await payrollApi.saveSettings({ ...settings, efka_employee_rate: Number(settings.efka_employee_rate), efka_employer_rate: Number(settings.efka_employer_rate),
        sick_first_days: Number(settings.sick_first_days), sick_first_pct: Number(settings.sick_first_pct), sick_after_pct: Number(settings.sick_after_pct) });
      setSettings(s); toast.success(t("settings_saved")); setShowSettings(false);
    } catch { toast.error(t("error")); }
  };

  const totals = rows.reduce((a, r) => ({ gross: a.gross + r.gross, net: a.net + r.net, cost: a.cost + r.employer_cost }), { gross: 0, net: 0, cost: 0 });

  return (
    <div className="space-y-4" data-testid="payslips-tab">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28 font-mono" data-testid="payroll-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-40" data-testid="payroll-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{monthName(m)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-48" data-testid="payroll-kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_kinds")}</SelectItem>
              {PAY_KINDS.map((k) => <SelectItem key={k} value={k}>{t(`kind_${k}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => setShowSettings((s) => !s)} data-testid="payroll-settings-button" title={t("payroll_settings")}><Settings2 size={16} /></Button>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={runKind} onValueChange={setRunKind}>
            <SelectTrigger className="w-48" data-testid="payroll-run-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PAY_KINDS.map((k) => <SelectItem key={k} value={k}>{t(`kind_${k}`)}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={run} disabled={running} className="bg-blue-800 hover:bg-blue-700" data-testid="run-payroll-button">
            <Play size={15} className="mr-1" /> {t("run_payroll")}
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">{t("run_payroll_hint")} {t("tax_note")}</p>

      {showSettings && settings && (
        <div className="erp-card p-4 flex flex-wrap items-end gap-4" data-testid="payroll-settings-panel">
          <div className="text-sm font-semibold font-heading">{t("payroll_settings")} (ΕΦΚΑ)</div>
          <div className="space-y-1"><div className="text-xs uppercase tracking-wider text-slate-500">{t("rate_employee")}</div>
            <Input type="number" step="0.01" value={settings.efka_employee_rate} onChange={(e) => setSettings({ ...settings, efka_employee_rate: e.target.value })} className="w-28 font-mono" data-testid="rate-employee-input" /></div>
          <div className="space-y-1"><div className="text-xs uppercase tracking-wider text-slate-500">{t("rate_employer")}</div>
            <Input type="number" step="0.01" value={settings.efka_employer_rate} onChange={(e) => setSettings({ ...settings, efka_employer_rate: e.target.value })} className="w-28 font-mono" data-testid="rate-employer-input" /></div>
          <div className="space-y-1"><div className="text-xs uppercase tracking-wider text-slate-500">{t("sick_rule")}</div>
            <Input type="number" min="0" value={settings.sick_first_days} onChange={(e) => setSettings({ ...settings, sick_first_days: e.target.value })} className="w-24 font-mono" data-testid="sick-first-days-input" /></div>
          <div className="space-y-1"><div className="text-xs uppercase tracking-wider text-slate-500">{t("sick_first_pct")}</div>
            <Input type="number" step="1" value={settings.sick_first_pct} onChange={(e) => setSettings({ ...settings, sick_first_pct: e.target.value })} className="w-24 font-mono" data-testid="sick-first-pct-input" /></div>
          <div className="space-y-1"><div className="text-xs uppercase tracking-wider text-slate-500">{t("sick_after_pct")}</div>
            <Input type="number" step="1" value={settings.sick_after_pct} onChange={(e) => setSettings({ ...settings, sick_after_pct: e.target.value })} className="w-24 font-mono" data-testid="sick-after-pct-input" /></div>
          <Button size="sm" onClick={saveSettings} className="bg-blue-800 hover:bg-blue-700" data-testid="rates-save-button">{t("save")}</Button>
        </div>
      )}

      <div className="erp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3">{t("employee")}</th>
                <th className="px-4 py-3">{t("kind")}</th>
                <th className="px-4 py-3 text-right">{t("gross")}</th>
                <th className="px-4 py-3 text-right hidden md:table-cell">ΕΦΚΑ</th>
                <th className="px-4 py-3 text-right hidden md:table-cell">{t("fmy")}</th>
                <th className="px-4 py-3 text-right">{t("net")}</th>
                <th className="px-4 py-3 text-right hidden lg:table-cell">{t("employer_cost")}</th>
                <th className="px-4 py-3">{t("status")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`payslip-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.employee_name}</div>
                    <div className="text-xs text-slate-500">{t(s.employment_type)} · {s.weekly_hours}h</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{t(`kind_${s.kind}`)}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {fmtMoney(s.gross)}
                    {(s.overtime_pay > 0 || s.sick_deduction > 0 || s.unpaid_deduction > 0) && (
                      <div className="text-[11px] text-slate-500" data-testid={`payslip-adjustments-${s.id}`}>
                        {s.overtime_pay > 0 && <span className="text-emerald-600">+{fmtMoney(s.overtime_pay)} </span>}
                        {(s.sick_deduction > 0 || s.unpaid_deduction > 0) && <span className="text-rose-600">−{fmtMoney(s.sick_deduction + s.unpaid_deduction)}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono hidden md:table-cell text-slate-500">− {fmtMoney(s.efka_employee)}</td>
                  <td className="px-4 py-3 text-right font-mono hidden md:table-cell text-slate-500">− {fmtMoney(s.tax)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-700 dark:text-emerald-300" data-testid={`payslip-net-${s.id}`}>{fmtMoney(s.net)}</td>
                  <td className="px-4 py-3 text-right font-mono hidden lg:table-cell">{fmtMoney(s.employer_cost)}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {s.status === "draft" && s.kind === "salary" && <button onClick={() => recalc(s)} title={t("recalculate")} data-testid={`payslip-recalc-${s.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><RefreshCw size={15} /></button>}
                      {s.status === "draft" && <button onClick={() => markPaid(s)} title={t("mark_paid")} data-testid={`payslip-pay-${s.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-emerald-100 dark:hover:bg-emerald-950 text-emerald-600"><CheckCircle2 size={15} /></button>}
                      <button onClick={() => setViewing(s)} data-testid={`payslip-view-${s.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><Eye size={15} /></button>
                      <button onClick={() => setDeleting(s)} data-testid={`payslip-delete-${s.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500" data-testid="payslips-empty">{t("no_results")}</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 dark:bg-slate-900/60 font-mono font-medium border-t-2 border-slate-200 dark:border-slate-700">
                  <td className="px-4 py-3 font-body font-semibold" colSpan={2}>{t("total")} · {rows.length}</td>
                  <td className="px-4 py-3 text-right">{fmtMoney(totals.gross)}</td>
                  <td className="hidden md:table-cell" colSpan={2}></td>
                  <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-300" data-testid="payslips-total-net">{fmtMoney(totals.net)}</td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell" data-testid="payslips-total-cost">{fmtMoney(totals.cost)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <PayslipDialog slip={viewing} onOpenChange={(o) => !o && setViewing(null)} />
      <ApdCard year={Number(year)} month={Number(month)} key={`${year}-${month}-${rows.length}`} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_payslip")} onConfirm={remove} />
    </div>
  );
};
